import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAuthDialog } from '../../contexts/AuthDialogContext';
import { type ReactNode, useEffect, useRef } from 'react';

interface AuthGuardProps {
  children: ReactNode;
  requireAuth?: boolean;
  publicOnly?: boolean; // Add this prop for public-only routes like login
  onAuthSuccess?: () => void; // Callback to execute after successful authentication
}

export function AuthGuard({
  children,
  requireAuth = false,
  publicOnly = false,
  onAuthSuccess,
}: AuthGuardProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { showAuthDialog } = useAuthDialog();

  // All hooks must be declared before any conditional returns
  const prevAuthenticatedRef = useRef(isAuthenticated);
  const hasCalledSuccessRef = useRef(false);

  // For protected routes when user is not authenticated
  useEffect(() => {
    if (requireAuth && !isAuthenticated && !isLoading) {
      showAuthDialog('login', onAuthSuccess);
    }
  }, [requireAuth, isAuthenticated, isLoading, showAuthDialog, onAuthSuccess]);

  // Execute callback when user becomes authenticated (only once)
  useEffect(() => {
    if (
      requireAuth &&
      isAuthenticated &&
      !isLoading &&
      onAuthSuccess &&
      !prevAuthenticatedRef.current &&
      !hasCalledSuccessRef.current
    ) {
      hasCalledSuccessRef.current = true;
      onAuthSuccess();
    }
    prevAuthenticatedRef.current = isAuthenticated;

    // Reset flag when user logs out
    if (!isAuthenticated) {
      hasCalledSuccessRef.current = false;
    }
  }, [requireAuth, isAuthenticated, isLoading, onAuthSuccess]);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="flex h-dvh md:min-h-screen md:max-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (requireAuth && !isAuthenticated) {
    // Show the children but the auth dialog will be displayed
    return <>{children}</>;
  }

  // For public-only routes (like login) when user is already authenticated
  if (publicOnly && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
